import PropTypes from "prop-types";
import React from "react";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, {
  PaginationListStandalone,
  PaginationProvider,
  SizePerPageDropdownStandalone,
} from "react-bootstrap-table2-paginator";
import { Col, Row } from "reactstrap";
import { formatInvoiceDate, formatMoney } from "../helper";

const SIZE_PER_PAGE_LIST = [10, 20, 50, 100];

const moneyColumn = (dataField, text) => ({
  dataField,
  text,
  align: "right",
  headerAlign: "right",
  formatter: cell => formatMoney(cell),
});

const InvoiceTable = props => {
  const {
    items,
    selectable,
    selectedIds,
    onSelect,
    onSelectAll,
    showReason,
    emptyText,
    page,
    sizePerPage,
    totalSize,
    onPageChange,
    onViewPdf,
  } = props;

  // Có onPageChange nghĩa là dữ liệu do API phân trang, bảng chỉ hiển thị
  // đúng trang hiện tại và báo ngược lên khi người dùng bấm đổi trang.
  const isRemote = typeof onPageChange === "function";

  // STT tính theo trang đang xem nên phải dựng cột lúc đã biết page/sizePerPage
  const buildColumns = (currentPage, currentSize) => {
    const columns = [
      {
        dataField: "__stt",
        text: "STT",
        isDummyField: true,
        align: "center",
        headerAlign: "center",
        headerStyle: { width: 60 },
        formatter: (cell, row, rowIndex) =>
          (currentPage - 1) * currentSize + rowIndex + 1,
      },
      { dataField: "serial", text: "Ký hiệu", sort: true },
      { dataField: "number", text: "Số HĐ", sort: true },
      {
        dataField: "date",
        text: "Ngày HĐ",
        sort: true,
        formatter: cell => formatInvoiceDate(cell),
      },
      {
        dataField: "buyer_name",
        text: "Người mua",
        headerStyle: { width: 260 },
        // Bảng đang table-nowrap nên phải cho ô này tự xuống dòng, kèm title
        // để hover xem đủ tên
        formatter: cell => (
          <div
            className="text-wrap"
            style={{ minWidth: 200, maxWidth: 260 }}
            title={cell || ""}
          >
            {cell || ""}
          </div>
        ),
      },
      {
        dataField: "buyer_tax_code",
        text: "MST",
        // Chỉ lấy mã số thuế thật, không lấy bù mã khách hàng (buyer_code)
        formatter: cell => cell || "",
      },
      moneyColumn("net", "Tiền chưa thuế"),
      moneyColumn("vat", "Tiền thuế"),
      moneyColumn("pay", "Tổng tiền"),
    ];

    if (onViewPdf) {
      columns.push({
        dataField: "__action",
        text: "Thao tác",
        isDummyField: true,
        align: "center",
        headerAlign: "center",
        headerStyle: { width: 90 },
        formatter: (cell, row) => (
          <button
            type="button"
            className="btn btn-sm btn-link text-danger p-0"
            title="Xem hóa đơn"
            aria-label="Xem hóa đơn"
            onClick={event => {
              // Bảng bật clickToSelect nên phải chặn click lan ra dòng
              event.stopPropagation();
              onViewPdf(row);
            }}
          >
            <i className="bx bxs-file-pdf font-size-18" />
          </button>
        ),
      });
    }

    if (showReason) {
      columns.push({
        dataField: "reason",
        text: "Lý do",
        formatter: (cell, row) => {
          const reason = cell || row.message || "";
          if (!reason) return "";
          return (
            <span className="badge badge-soft-danger rounded-pill font-size-12 text-wrap text-start">
              <i className="bx bx-error-circle me-1 align-middle" />
              {reason}
            </span>
          );
        },
      });
    }

    return columns;
  };

  const selectRow = selectable
    ? {
        mode: "checkbox",
        clickToSelect: true,
        selected: selectedIds,
        onSelect,
        onSelectAll,
      }
    : undefined;

  if (!items || items.length === 0) {
    return <div className="text-center text-muted py-4">{emptyText}</div>;
  }

  const pageOptions = isRemote
    ? {
        page,
        sizePerPage,
        totalSize,
        sizePerPageList: SIZE_PER_PAGE_LIST,
        custom: true,
      }
    : {
        sizePerPage: 10,
        totalSize: items.length,
        custom: true,
      };

  const handleTableChange = (type, newState) => {
    if (type === "pagination") {
      onPageChange(newState.page, newState.sizePerPage);
    }
  };

  return (
    <PaginationProvider
      pagination={paginationFactory(pageOptions)}
      keyField="id"
      columns={buildColumns(pageOptions.page || 1, pageOptions.sizePerPage)}
      data={items}
    >
      {({ paginationProps, paginationTableProps }) => (
        <React.Fragment>
          <Row>
            <Col xl="12">
              <div className="table-responsive">
                <BootstrapTable
                  keyField="id"
                  responsive
                  bordered={false}
                  striped={false}
                  columns={buildColumns(
                    paginationProps.page,
                    paginationProps.sizePerPage
                  )}
                  data={items}
                  selectRow={selectRow}
                  remote={isRemote ? { pagination: true } : undefined}
                  onTableChange={isRemote ? handleTableChange : undefined}
                  classes="table align-middle table-nowrap"
                  headerWrapperClasses="thead-light"
                  {...paginationTableProps}
                />
              </div>
            </Col>
          </Row>
          <Row className="align-items-md-center mt-3">
            <Col className="inner-custom-pagination d-flex">
              <div className="d-inline">
                <SizePerPageDropdownStandalone {...paginationProps} />
              </div>
              <div className="text-md-right ms-auto">
                <PaginationListStandalone {...paginationProps} />
              </div>
            </Col>
          </Row>
        </React.Fragment>
      )}
    </PaginationProvider>
  );
};

InvoiceTable.propTypes = {
  items: PropTypes.array,
  selectable: PropTypes.bool,
  selectedIds: PropTypes.array,
  onSelect: PropTypes.func,
  onSelectAll: PropTypes.func,
  showReason: PropTypes.bool,
  emptyText: PropTypes.string,
  page: PropTypes.number,
  sizePerPage: PropTypes.number,
  totalSize: PropTypes.number,
  onPageChange: PropTypes.func,
  onViewPdf: PropTypes.func,
};

InvoiceTable.defaultProps = {
  items: [],
  selectable: false,
  selectedIds: [],
  onSelect: undefined,
  onSelectAll: undefined,
  showReason: false,
  emptyText: "Không có hóa đơn",
  page: 1,
  sizePerPage: 10,
  totalSize: 0,
  onPageChange: undefined,
  onViewPdf: undefined,
};

export default InvoiceTable;
